/**
 * A snap held up before it goes, full screen, to be written on and stuck with
 * emojis -- the way Snapchat does it.
 *
 * The picture fills the screen on a stage its own shape, with itself blurred
 * behind it, and everything else floats on it: close at the top, the tools
 * down the right, Retake and Send along the bottom. The writing itself is
 * useDecorEditor's, shared with the GiviSnap.
 *
 * The stage here is the viewer's, so what is laid on it lands where it was
 * put, on a clip as well as a photo.
 *
 * It opens over the sheet it came from and is still inside that sheet as far
 * as React is concerned, so its presses stop at its edge: a drag on it must
 * never read as a pull on the sheet. (Its taps stop at the sheet's own edge,
 * which never lets a tap through to its backdrop.)
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import type { SnapDecor } from '../../../shared/types';
import { useDecorEditor } from '../DecorEditor';
import { reachOf } from '../../../shared/snapDecor';
import { filterCss, filterLabel, nextFilter, type SnapFilter } from '../../../shared/snapFilters';
import { PhotoBackdrop, SnapStage, paintBackdrop, ratioOf } from '../SnapDecor';
import { useBackCloses } from '../router';
import { IconClose, IconFace, IconSend } from '../ui';
import { findFaces, type Face } from '../faces';
import { FACE_LOOKS, lookOnAll, type FaceLook } from '../../../shared/faceStickers';
import { artUrl } from '../../../shared/stickerArt';

export interface Shot {
  file: File;
  preview: string;
  video: boolean;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

export function SnapCompose({
  shot,
  to,
  onRetake,
  onDiscard,
  onSend,
}: {
  shot: Shot;
  /** Who it goes to, for the send button. */
  to: string;
  onRetake: () => void;
  /** Put it down without sending, back to the choice it came from. */
  onDiscard: () => void;
  onSend: (decor: SnapDecor) => void;
}) {
  const [ratio, setRatio] = useState<number | null>(null);
  const backdrop = useRef<HTMLCanvasElement>(null);
  /** The look swiped to, and its name flashed up for a moment when it changes. */
  const [filter, setFilter] = useState<SnapFilter | undefined>(undefined);
  const [flash, setFlash] = useState<{ label: string; key: number } | null>(null);
  const editor = useDecorEditor({
    // Anywhere on the screen, the blurred fill round the picture included.
    reach: reachOf(ratio ?? ratioOf(undefined, undefined)),
    onSwipe: (step) => {
      const next = nextFilter(filter, step);
      setFilter(next);
      setFlash({ label: filterLabel(next), key: Date.now() });
    },
  });
  /*
   * Face stickers: the photo's faces found on the phone (../faces), then a
   * look picked from a strip and laid on every one of them. Photos only -- a
   * clip's faces move.
   */
  const shown = useRef<HTMLImageElement>(null);
  const [faces, setFaces] = useState<'finding' | Face[] | null>(null);
  const [faceNote, setFaceNote] = useState<string | null>(null);
  const openFaces = async () => {
    const image = shown.current;
    if (!image) return;
    setFaceNote(null);
    setFaces('finding');
    try {
      const found = await findFaces(image);
      if (found.length === 0) {
        setFaces(null);
        setFaceNote('No faces found in this one.');
      } else {
        setFaces(found);
      }
    } catch {
      setFaces(null);
      setFaceNote('Faces did not load. Try again.');
    }
  };
  const lay = (look: FaceLook) => {
    if (!Array.isArray(faces)) return;
    editor.addStickers(lookOnAll(faces, look, ratio ?? ratioOf(undefined, undefined), editor.room));
    setFaces(null);
  };
  useEffect(() => {
    if (!faceNote) return;
    const off = window.setTimeout(() => setFaceNote(null), 2400);
    return () => window.clearTimeout(off);
  }, [faceNote]);

  useEffect(() => {
    if (!flash) return;
    const off = window.setTimeout(() => setFlash(null), 900);
    return () => window.clearTimeout(off);
  }, [flash]);
  useBackCloses(onDiscard);

  return createPortal(
    <div
      className="snap-editor"
      role="dialog"
      aria-label={`Write on your snap to ${to}`}
      data-filter={filter}
      style={{ '--look': filterCss(filter) } as CSSProperties}
      onPointerDown={stop}
      onPointerMove={stop}
      onPointerUp={stop}
      onPointerCancel={stop}
    >
      {shot.video ? (
        <canvas ref={backdrop} className="snap-backdrop" aria-hidden="true" />
      ) : (
        <PhotoBackdrop src={shot.preview} />
      )}
      <div className="snap-editor-canvas" {...editor.surface}>
        <SnapStage ratio={ratio ?? ratioOf(undefined, undefined)}>
          {shot.video ? (
            <video
              className="snap-stage-media"
              src={shot.preview}
              playsInline
              autoPlay
              loop
              muted
              onLoadedMetadata={(event) =>
                setRatio(ratioOf(event.currentTarget.videoWidth, event.currentTarget.videoHeight))
              }
              onLoadedData={(event) => paintBackdrop(backdrop.current, event.currentTarget)}
            />
          ) : (
            <img
              ref={shown}
              className="snap-stage-media"
              src={shot.preview}
              alt=""
              draggable={false}
              onLoad={(event) =>
                setRatio(ratioOf(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))
              }
            />
          )}
          {editor.layer}
        </SnapStage>
      </div>

      {editor.blank && !flash && (
        <span className="snap-editor-hint" aria-hidden="true">
          Tap to write on it · swipe for filters
        </span>
      )}
      {flash && (
        <span key={flash.key} className="snap-filter-name" role="status">
          {flash.label}
        </span>
      )}

      <button type="button" className="snap-editor-key snap-editor-close" aria-label="Discard" onClick={onDiscard}>
        <IconClose size={20} />
      </button>

      <div className="snap-editor-tools">
        {editor.tools}
        {!shot.video && (
          <button
            type="button"
            className="snap-editor-key"
            aria-label="Face stickers"
            aria-pressed={faces !== null}
            disabled={editor.room === 0 || faces === 'finding'}
            onClick={() => void openFaces()}
          >
            <IconFace size={20} />
          </button>
        )}
      </div>
      {faces === 'finding' && (
        <span className="snap-face-note" role="status">
          Finding faces…
        </span>
      )}
      {faceNote && (
        <span className="snap-face-note" role="status">
          {faceNote}
        </span>
      )}
      {Array.isArray(faces) && (
        <div className="snap-face-strip" role="group" aria-label={`Stickers for ${faces.length === 1 ? 'the face' : `${faces.length} faces`}`}>
          {FACE_LOOKS.map((look) => (
            <button key={look.name} type="button" className="snap-face-look" aria-label={look.name} onClick={() => lay(look)}>
              {'art' in look.preview ? <img src={artUrl(look.preview.art)} alt="" draggable={false} /> : look.preview.emoji}
            </button>
          ))}
          <button type="button" className="snap-face-look snap-face-done" aria-label="Close face stickers" onClick={() => setFaces(null)}>
            <IconClose size={18} />
          </button>
        </div>
      )}

      <div className="snap-editor-foot">
        <button type="button" className="snap-editor-retake" onClick={onRetake}>
          Retake
        </button>
        <button
          type="button"
          className="snap-editor-send"
          aria-label="Send snap"
          onClick={() => onSend({ ...editor.decor, ...(filter ? { filter } : {}) })}
        >
          Send to {to} <IconSend size={18} />
        </button>
      </div>

      {editor.overlays}
    </div>,
    document.body,
  );
}
